import { useIDTagging } from '@msdk/common'
import {
  Entity,
  RowActionBehaviour,
  RowClickModeDefiniton,
  ViewColumnBehaviour,
  ViewColumnDetail,
} from '@msdk/definitions'
import {
  ButtonIcon,
  Checkbox,
  Collapse,
  COLORS,
  KeyboardArrowDownIcon,
  KeyboardArrowUpIcon,
  Radio,
  styled,
  TableCell,
  TableRow,
} from '@msdk/ui'
import React, { useState } from 'react'

import { DetailView } from '../DetailView'
import { ConditionalCellRenderer } from './cell-renderers'
import { useDynamicViewContext } from './context'
import { RowActions } from './RowActions'
import {
  ActionsTableCell,
  CheckboxTableCell,
  DetailViewTableCell,
  EmptyTableCell,
  getDynamicViewFieldDefinition,
  StyledTableCell,
} from './utils'

const CollapseTableCell = styled(TableCell)`
  padding: 0 0 0 6%;
  width: 100%;
`

const DetailButtonIcon = styled(ButtonIcon)`
  height: 100%;

  aspect-ratio: unset;
  &:not(:has(.MuiTypography-root)) {
    aspect-ratio: unset;
  }

  &:focus,
  &:active,
  &:hover {
    background-color: unset;
  }
`

const StyledDetailViewTableRow = styled(TableRow)`
  border-radius: 0;

  &:hover,
  &.MuiTableRow-hover:hover {
    background-color: inherit;
  }

  && > td {
    padding: 0;
    background-color: ${COLORS.components.tab.content.background};
  }
`

interface Props {
  row: Entity
}

export function Row({ row }: Props) {
  const {
    anyRowHasActions,
    baseMetadata,
    columns,
    multiSelect,
    lookup,
    onRowClick,
    onRowSelected,
    selectedRows,
    viewModel,
    visibleColumns,
    pinned,
    styleMode,
    showExtra,
    getColumnWidth,
    getFreeSpace,
    areRowsClickable,
    isEmbedded,
  } = useDynamicViewContext()

  const idTaggingEnabled = useIDTagging()
  const [collapseOpen, setCollapseOpen] = useState<boolean>(false)

  const onArrowClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setCollapseOpen(!collapseOpen)
  }

  const someColumnsAreClickable = viewModel.columns.some((e) => e.behaviour === ViewColumnBehaviour.Clickable)
  const isRowClickable =
    !someColumnsAreClickable &&
    !!onRowClick &&
    (areRowsClickable !== undefined ? areRowsClickable : viewModel.rowClickMode !== RowClickModeDefiniton.Unclickable)

  const isItemClickable = someColumnsAreClickable && !!onRowClick

  const handleRowClick = () => {
    if (isRowClickable) {
      onRowClick(row)
    }
  }

  const handleItemClick = () => {
    if (isItemClickable) {
      onRowClick(row)
    }
  }

  const isCheckboxSelected = () => {
    return selectedRows?.findIndex((r) => r.id === row.id) !== -1
  }

  const toggleRowMultiselection = () => {
    const index = selectedRows?.findIndex((r) => r.id === row.id)
    if (index !== -1) {
      const updated = selectedRows?.filter((r) => r.id !== row.id) as Entity[]
      if (onRowSelected) onRowSelected(updated)
    } else {
      const updated = [...(selectedRows ?? []), row]
      if (onRowSelected) onRowSelected(updated)
    }
  }

  const toggleRowSingleselection = () => {
    if (onRowSelected) onRowSelected([row])
  }

  const shouldShowRowActions = viewModel.rowActions.behaviour !== RowActionBehaviour.Hide

  const mode = styleMode ?? 'Default'

  return (
    <>
      <TableRow
        isEmbedded={isEmbedded}
        cursor={isRowClickable ? 'pointer' : 'default'}
        key={row.id}
        onClick={handleRowClick}
      >
        {viewModel.detailView && (
          <DetailViewTableCell data-col-name="DETAIL">
            <DetailButtonIcon onClick={onArrowClick}>
              {collapseOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </DetailButtonIcon>
          </DetailViewTableCell>
        )}
        {lookup && !multiSelect && (
          <CheckboxTableCell data-col-name="SELECT" $visible>
            <Radio
              checked={isCheckboxSelected()}
              onClick={(e) => {
                e.stopPropagation()
                toggleRowSingleselection()
              }}
            />
          </CheckboxTableCell>
        )}
        {multiSelect && (
          <CheckboxTableCell data-col-name="SELECT" $visible>
            <Checkbox
              color="primary"
              checked={isCheckboxSelected()}
              onClick={(e) => {
                e.stopPropagation()
                toggleRowMultiselection()
              }}
            />
          </CheckboxTableCell>
        )}
        {visibleColumns.map((column, i) => (
          <StyledTableCell
            $idTaggingEnabled={idTaggingEnabled}
            data-col-name={column.fieldName}
            key={column.fieldName}
            scope="row"
            size="small"
            $width={getColumnWidth(column.fieldName)}
            $first={i === 0}
            $pinned={pinned}
            $isExtra={column.detail === ViewColumnDetail.Extra}
            styleMode={mode}
            $visible={column.visibility && (column.detail !== ViewColumnDetail.Extra || showExtra)}
            $alignment={column.alignment}
          >
            {!column.customUI && (
              <ConditionalCellRenderer
                value={row.fields[column.fieldName]}
                col={column}
                field={getDynamicViewFieldDefinition(viewModel, baseMetadata, column.fieldName).fieldDefinitionType}
                onItemClick={handleItemClick}
              />
            )}
            {column.customUI && (
              <ConditionalCellRenderer
                value={row.fields[column.fieldName]}
                row={row}
                col={column}
                field="Custom"
                onItemClick={handleItemClick}
              />
            )}
          </StyledTableCell>
        ))}
        <EmptyTableCell data-col-name="EMPTY" $width={getFreeSpace()} $visible />
        {/** ! This should be refactored in the near future. */}
        {shouldShowRowActions && (
          <ActionsTableCell
            data-col-name="ACTIONS"
            className="action-row"
            shouldShow={anyRowHasActions}
            styleMode={styleMode}
          >
            <RowActions row={row} />
          </ActionsTableCell>
        )}
      </TableRow>
      {viewModel.detailView && (
        <StyledDetailViewTableRow>
          <CollapseTableCell colSpan={columns.length + 1}>
            <Collapse in={collapseOpen} timeout="auto" unmountOnExit>
              <DetailView
                entityName={viewModel.detailView.entityName}
                viewName={viewModel.detailView.name}
                containerEntity={row}
                viewDef={viewModel.detailView}
              />
            </Collapse>
          </CollapseTableCell>
        </StyledDetailViewTableRow>
      )}
    </>
  )
}
